import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Button, Menu, Message, Popconfirm } from '@arco-design/web-react';
import * as Icon from '@arco-design/web-react/icon';

import './App.css';

import AdminManagement from './admin/index';
import Announcement from './announcement/index';
import icon from './assets/icon.png';
import Clientmenu from './clientmenu/index';
import Comment from './comment/index';
import Composition from './composition/index';
import Config from './config/index';
import CouponConfig from './coupon/index';
import UserCouponManagement from './coupon/user';
import Error from './error/index';
import EventCalendarConfig from './event-calendar-config/index';
import EventManagement from './event-management/index';
import FansTeam from './fans-team/index';
import GameManagement from './game/index';
import GameGroupQrcode from './game-qrcode/index';
import Login from './login/index';
import Main from './main/index';
import Medal from './medal/index';
import MemberContentManagement from './member-content/index';
import MemberOverview from './member-overview/index';
import MenuManagement from './menu/index';
import MessageList from './message-list/index';
import Note from './note/index';
import NoteStatistics from './note/statistics';
import NotebookReport from './notebook-report/index';
import Order from './order/index';
import Pay from './pay/index';
import PayActivity from './pay-activity/index';
import PayOrder from './pay-order/index';
import AppleIapNotification from './apple-iap-notification/index';
import PenaltyLog from './penalty-log/index';
import PenaltyCancelApply from './penalty-cancel-apply/index';
import BanList from './ban-list/index';
import RankTopComment from './rankTopComment/index';
import { get, post } from './requestNew';
import ResourceManagement from './resource/index';
import ResourceCategoryManagement from './resource-category/index';
import RoleManagement from './role/index';
import SectorsManagement from './sectors-management/index';
import StockManagement from './stock-management/index';
import AgentThemeManagement from './agent-theme-management/index';
import AgentStarStockManagement from './agent-star-stock-management';
import AgentReasonAudit from './agent-reason-audit';
import AgentMessageManagement from './agent-message-management/index';
import Topics from './topics/index';
import UserManagement from './user/index';
import UserAction from './user-action/index';
import UserDevice from './user-device/index';
import UserMember from './user-member/index';
import ReviewMemberContent from './review-member-content/index';
import ReviewMemberOverview from './review-member-overview/index';
import ReviewUserMember from './review-user-member/index';
import ReviewGroupQrcode from './review-group-qrcode/index';
import Welcome from './welcome/index';

/** AI 生图、海报编辑器体积较大，按需加载，不拖慢首屏 */
const ImageChat = React.lazy(() => import('./ai-image-chat/index'));
const PosterEditor = React.lazy(() => import('./poster-editor/index'));

const MenuItem = Menu.Item;
const SubMenu = Menu.SubMenu;

type Option = {
  label: string;
  value: string;
};

const isOverviewPath = (path: string) => path === '/welcome' || path === '/operations-dashboard';

// 递归检查路径是否在菜单树中
const isPathAllowed = (path: string, menuList: any[]): boolean => {
  if (!menuList || menuList.length === 0) return false;

  for (const item of menuList) {
    // 检查当前项的路径是否匹配
    if (item.path === path && item.hidden === 0) {
      return true;
    }
    // 如果有子菜单，递归检查
    if (item.children && item.children.length > 0) {
      if (isPathAllowed(path, item.children)) {
        return true;
      }
    }
  }
  return false;
};

// 递归查找给定路径的父级菜单路径
const findParentMenuPath = (targetPath: string, menuList: any[], parentPath?: string): string | null => {
  if (!menuList || menuList.length === 0) return null;

  for (const item of menuList) {
    // 如果当前项有子菜单，检查子菜单中是否包含目标路径
    if (item.children && item.children.length > 0) {
      // 检查子菜单中是否有目标路径
      const foundInChildren = item.children.some((child: any) => child.path === targetPath && child.hidden === 0);

      if (foundInChildren) {
        // 找到了，返回当前项的路径（父级菜单路径）
        return item.path;
      }

      // 递归查找子菜单
      const parentPathFound = findParentMenuPath(targetPath, item.children, item.path);
      if (parentPathFound) {
        return parentPathFound;
      }
    }
  }
  return null;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKey, setKey] = useState('');
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [groupTypeOption, setOption] = useState<Option[]>([]);
  const [groupTypeMap, setMap] = useState({});
  const [menuTreeList, setMenuTreeList] = useState<any[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [userInfo, setUserInfo] = useState<any>({});
  const [hasError, setHasError] = useState(false);

  // 获取当前登录用户信息
  const getUserInfo = () => {
    // 如果已经出错，不再请求接口
    // if (hasError) return;

    get('/admin/info')
      .then((res: any) => {
        if (res && res.data) {
          setUserInfo(res.data);
        }
      })
      .catch((err: any) => {
        console.log('获取用户信息失败:', err);
      });
  };

  // 拿到列表之后才执行获取分组信息
  const getMenuTreeList = () => {
    get('/menu/user/treeList')
      .then((res: any) => {
        // 获取菜单后获取用户信息
        getUserInfo();
        if (res && res.data && res.data.length > 0) {
          setMenuTreeList(res.data);
          getGroupTypeBySession();
          setHasError(false);
        } else {
          setHasError(true);
          if (!isOverviewPath(location.pathname)) {
            navigate('/error', { replace: true });
          }
        }
      })
      .catch((err: any) => {
        console.log(err);
        setHasError(true);
        if (!isOverviewPath(location.pathname)) {
          navigate('/error', { replace: true });
        }
      });
  };

  const getGroupTypeBySession = () => {
    // 如果已经出错，不再请求接口
    if (hasError) return;

    get('/groupType/getGroupTypeBySession', { gameSession: '5' }).then((res: any) => {
      if (res && res.data) {
        const groupTypeMap: Record<string, string> = {};
        const list = res.data.map((item: any) => {
          const { groupType, groupTypeName } = item;
          groupTypeMap[groupType] = groupTypeName;
          return {
            label: groupTypeName,
            value: groupType
          };
        });

        setOption(list);
        setMap(groupTypeMap);
      }
    });
  };

  // 根据字符串动态获取图标组件
  const getIconComponent = (iconName?: string) => {
    if (!iconName) return null;
    const IconComponent = (Icon as any)[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  // 递归渲染菜单项
  const renderMenuItems = (menuList: any[]) => {
    return menuList
      .filter(item => item.hidden === 0 && !isOverviewPath(item.path))
      .map(item => {
        const { title, path, children, icon } = item;
        const hasChildren = children && children.length > 0;
        if (hasChildren) {
          return (
            <SubMenu
              key={path}
              title={
                <>
                  {getIconComponent(icon)}
                  {title}
                </>
              }
            >
              {renderMenuItems(children)}
            </SubMenu>
          );
        } else {
          return (
            <MenuItem key={path}>
              {getIconComponent(icon)}
              {title}
            </MenuItem>
          );
        }
      });
  };

  // 路由拦截逻辑
  useEffect(() => {
    const currentPath = location.pathname;
    const token = localStorage.getItem('authToken');
    // 更新菜单选中状态
    setKey(currentPath);

    // login 和 error 路由不需要拦截，直接放行
    // if (currentPath === '/menu' || currentPath === '/role' || currentPath === '/admin' || currentPath === '/resource' || currentPath === '/resource-category') {
    //   return;
    // }

    // login 和 error 路由不需要拦截，直接放行
    if (currentPath === '/login' || currentPath === '/error') {
      return;
    }

    // 如果菜单列表为空（未登录或数据未加载），重定向到登录页
    if (!token && (!menuTreeList || menuTreeList.length === 0)) {
      navigate('/login', { replace: true });
      return;
    }

    // 如果已登录但菜单列表为空，且没有错误，加载菜单数据
    if (token && (!menuTreeList || menuTreeList.length === 0) && !hasError) {
      getMenuTreeList();
      // 工作台页面允许继续，其他页面需要等待菜单加载
      if (!isOverviewPath(currentPath)) {
        return;
      }
    }

    // 工作台路由直接放行，接口仍由后端进行认证和权限校验
    if (isOverviewPath(currentPath)) {
      return;
    }

    // 如果已登录 且 路径不在菜单中，重定向到欢迎页
    if (token && !isPathAllowed(currentPath, menuTreeList)) {
      navigate('/welcome', { replace: true });
      return;
    }

    // 如果当前路径是二级菜单，找到对应的一级菜单并展开
    if (token && menuTreeList && menuTreeList.length > 0 && currentPath !== '/login' && currentPath !== '/welcome') {
      const parentPath = findParentMenuPath(currentPath, menuTreeList);
      if (parentPath) {
        // 如果找到了父级菜单，将其添加到展开列表中
        setOpenKeys([parentPath]);
      }
    }
  }, [location.pathname, menuTreeList, navigate, hasError]);

  // 处理登出
  const handleLogout = () => {
    post('/admin/logout')
      .then((res: any) => {
        if (res && res.code === 200) {
          localStorage.removeItem('authToken');
          Message.success('登出成功');
          navigate('/login', { replace: true });
          // 刷新页面
          window.location.reload();
        } else {
          // 即使接口失败，也清除 token 并跳转
          localStorage.removeItem('authToken');
          navigate('/login', { replace: true });
          // 刷新页面
          window.location.reload();
        }
      })
      .catch((err: any) => {
        // 即使接口失败，也清除 token 并跳转
        localStorage.removeItem('authToken');
        navigate('/login', { replace: true });
        // 刷新页面
        window.location.reload();
        console.log('登出失败:', err);
      });
  };

  const IconMenuUnfold = getIconComponent('IconMenuUnfold');
  const IconMenuFold = getIconComponent('IconMenuFold');
  const isLoginPage = location.pathname === '/login';
  const isErrorPage = location.pathname === '/error';

  const showChrome = !isLoginPage && !isErrorPage;

  return (
    <div className={`app ${showChrome ? 'with-header' : ''}`}>
      {showChrome && (
        <header className="app-header">
          <div className="app-brand"><img src={icon} alt="福利" /><span>赛事运营管理平台</span></div>
          <button className="app-header-message" onClick={() => navigate('/messageList')}>
            <Icon.IconNotification />消息
          </button>
        </header>
      )}
      <div className="main-content">
        {!isLoginPage && !isErrorPage && (
          <div className={`menu ${collapsed ? 'collapsed' : ''}`}>
            <div className="logo">
              <img src={icon} style={{ display: collapsed ? 'none' : 'block' }} />
            </div>
            <Menu
              mode="vertical"
              collapse={collapsed}
              style={{ minWidth: collapsed ? 60 : 180 }}
              selectedKeys={[selectedKey]}
              openKeys={openKeys}
              onClickMenuItem={path => {
                if (isOverviewPath(path) || isPathAllowed(path, menuTreeList)) {
                  navigate(path);
                  setKey(path);
                }
              }}
              onClickSubMenu={(key: string, openKeys: string[]) => {
                setOpenKeys(openKeys);
              }}
            >
              <MenuItem key="/welcome"><Icon.IconHome />首页工作台</MenuItem>
              {renderMenuItems(menuTreeList)}
            </Menu>
            <div className="menu-toggle-container">
              <Button
                type="dashed"
                className="menu-toggle"
                onClick={() => setCollapsed(!collapsed)}
                aria-label={collapsed ? '展开菜单' : '折叠菜单'}
                icon={collapsed ? IconMenuUnfold : IconMenuFold}
              />
            </div>
          </div>
        )}
        <div className="router">
          {!isLoginPage && !isErrorPage && (
            <div className="userinfo-bar">
              <Popconfirm
                title={null}
                content={
                  <div className="user-info-popconfirm">
                    <div className="user-info-item">
                      <span className="user-info-label">用户名：</span>
                      <span className="user-info-value">{userInfo.nickName || userInfo.username || '-'}</span>
                    </div>
                    {userInfo.roles && userInfo.roles.length > 0 && (
                      <div className="user-info-item">
                        <span className="user-info-label">角色权限：</span>
                        <div className="user-info-roles">
                          {userInfo.roles.map((role: string, index: number) => (
                            <span key={index} className="user-role-tag">
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="user-info-actions">
                      <span
                        className="logout-text"
                        onClick={e => {
                          e.stopPropagation();
                          handleLogout();
                        }}
                      >
                        退出登录
                      </span>
                    </div>
                  </div>
                }
                trigger="click"
                position="br"
                okButtonProps={{ style: { display: 'none' } }}
                cancelButtonProps={{ style: { display: 'none' } }}
              >
                <div className="userinfo">
                  <div className="userinfo-avatar">
                    <img
                      src={
                        userInfo.icon ||
                        userInfo.avatar ||
                        'https://fulicupdev.oss-cn-hangzhou.aliyuncs.com/profilePic/defaultProfile.png'
                      }
                      alt="avatar"
                    />
                  </div>
                  <div className="userinfo-name">{userInfo.nickName || userInfo.username || '未登录'}</div>
                  <Icon.IconDown className="userinfo-arrow" />
                </div>
              </Popconfirm>
            </div>
          )}
          <div className="router-content">
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Main groupTypeOption={groupTypeOption} />} />
              <Route path="/pay" element={<Pay groupTypeOption={groupTypeOption} groupTypeMap={groupTypeMap} />} />
              <Route path="/pay-activity" element={<PayActivity />} />
              <Route path="/pay-order" element={<PayOrder />} />
              <Route path="/apple-iap-notification" element={<AppleIapNotification />} />
              <Route path="/composition" element={<Composition />} />
              <Route path="/note" element={<Note />} />
              <Route path="/note/statistics" element={<NoteStatistics />} />
              <Route path="/order" element={<Order />} />
              <Route path="/config" element={<Config />} />
              <Route path="/comment" element={<Comment />} />
              <Route path="/notebook-report" element={<NotebookReport />} />
              <Route path="/rankTopComment" element={<RankTopComment />} />
              <Route path="/announcement" element={<Announcement />} />
              <Route path="/messageList" element={<MessageList />} />
              <Route path="/clientmenu" element={<Clientmenu />} />
              <Route path="/game/index" element={<GameManagement />} />
              <Route path="/game/qrcode" element={<GameGroupQrcode />} />
              <Route path="/user" element={<UserManagement />} />
              <Route path="/admin" element={<AdminManagement />} />
              <Route path="/role" element={<RoleManagement />} />
              <Route path="/menu" element={<MenuManagement />} />
              <Route path="/resource" element={<ResourceManagement />} />
              <Route path="/resource-category" element={<ResourceCategoryManagement />} />
              <Route path="/userAction" element={<UserAction />} />
              <Route path="/userDevice" element={<UserDevice />} />
              <Route path="/fans-team" element={<FansTeam />} />
              <Route path="/topics" element={<Topics userInfo={userInfo} />} />
              <Route path="/coupon/config" element={<CouponConfig />} />
              <Route path="/coupon/user" element={<UserCouponManagement />} />
              <Route path="/login" element={<Login />} />
              <Route path="/welcome" element={<Welcome username={userInfo.nickName || userInfo.username} />} />
              <Route path="/medal" element={<Medal />} />
              <Route path="/userMember" element={<UserMember />} />
              <Route path="/member/overview" element={<MemberOverview />} />
              <Route path="/member/content" element={<MemberContentManagement />} />
              <Route path="/review/content" element={<ReviewMemberContent />} />
              <Route path="/review/overview" element={<ReviewMemberOverview />} />
              <Route path="/review/user" element={<ReviewUserMember />} />
              <Route path="/review/group-qrcode" element={<ReviewGroupQrcode />} />
              <Route path="/order/penalty-log" element={<PenaltyLog />} />
              <Route path="/order/penalty-cancel-apply" element={<PenaltyCancelApply />} />
              <Route path="/ban-list" element={<BanList />} />
              <Route path="/event-management" element={<EventManagement />} />
              <Route path="/event-calendar-config" element={<EventCalendarConfig />} />
              <Route path="/sectors-management" element={<SectorsManagement />} />
              <Route path="/stock-management" element={<StockManagement />} />
              <Route path="/agent-theme-management" element={<AgentThemeManagement />} />
              <Route path="/agent-star-stock-management" element={<AgentStarStockManagement />} />
              <Route path="/agent-reason-audit" element={<AgentReasonAudit />} />
              <Route path="/agent-message-management" element={<AgentMessageManagement />} />
              <Route path="/image-chat" element={<ImageChat />} />
              <Route path="/poster-editor" element={<PosterEditor />} />
              <Route path="/error" element={<Error />} />
              {/* 捕获所有未匹配的路由，重定向到欢迎页面 */}
              <Route path="*" element={<Navigate to={'/welcome'} replace />} />
            </Routes>
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
